'use client'

import Image from 'next/image'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import { ArrowRight } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { captureAnalyticsEvent } from '@/lib/analytics'

const WeekendBookingSection = dynamic(
  () => import('@/components/weekend/weekend-booking-section').then((m) => m.WeekendBookingSection),
  { ssr: false, loading: () => <p className="py-8 text-center text-sm text-muted-foreground">Loading booking…</p> },
)

const THUMBS = [
  { src: '/weekend-trips/betel-farm-corridor.webp' },
  { src: '/weekend-trips/silk-weaving-loom.webp' },
  { src: '/weekend-trips/doll-making-finished.png' },
]

export function WeekendAnnouncement() {
  const [seatsPerDay, setSeatsPerDay] = useState(10)
  const [open, setOpen] = useState(false)

  // Fetch seat count lazily only when the dialog opens — keeps homepage light.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    import('@/lib/weekend-booking').then(({ fetchWeekendTrip }) =>
      fetchWeekendTrip('thanjavur-weekend')
        .then((trip) => {
          if (trip && !cancelled) setSeatsPerDay(trip.slots_per_day)
        })
        .catch(() => {}),
    )
    return () => {
      cancelled = true
    }
  }, [open])

  const line = `Weekend in Thanjavur · every Sat & Sun · ${seatsPerDay} seats a day · tap to request a date`

  const openBooking = () => {
    captureAnalyticsEvent('site_button_clicked', {
      label: 'weekend_announcement',
      destination: 'weekend-booking-dialog',
      source: 'homepage',
    })
    setOpen(true)
  }

  return (
    <>
      <div className="fixed inset-x-0 top-[calc(4.5rem+env(safe-area-inset-top))] z-40 border-b border-white/10 bg-primary text-primary-foreground">
        <div className="site-shell flex h-11 items-center gap-3">
          <div
            aria-hidden="true"
            className="flex shrink-0 -space-x-2 [mask-image:linear-gradient(to_right,black_55%,transparent_98%)]"
          >
            {THUMBS.map((thumb) => (
              <Image
                key={thumb.src}
                src={thumb.src}
                alt=""
                width={56}
                height={56}
                className="size-7 rounded-full object-cover ring-2 ring-primary"
              />
            ))}
          </div>

          <button
            type="button"
            onClick={openBooking}
            aria-haspopup="dialog"
            aria-label="Plan your Thanjavur weekend trip"
            className="group flex min-w-0 flex-1 items-center overflow-hidden text-left"
          >
            <div className="flex w-max shrink-0 items-center [animation:oorva-announcement-marquee_32s_linear_infinite] group-hover:[animation-play-state:paused] motion-reduce:[animation:none]">
              <span className="shrink-0 whitespace-nowrap pr-10 text-xs font-semibold tracking-wide">
                {line}
              </span>
              <span aria-hidden="true" className="shrink-0 whitespace-nowrap pr-10 text-xs font-semibold tracking-wide">
                {line}
              </span>
            </div>
          </button>

          <Button
            size="sm"
            variant="secondary"
            onClick={openBooking}
            className="hidden h-7 shrink-0 rounded-full px-3 text-xs md:inline-flex"
          >
            Plan <ArrowRight className="size-3.5" />
          </Button>
        </div>
      </div>
      <div className="h-11" aria-hidden="true" />
      <style>{`@keyframes oorva-announcement-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-4xl" data-lenis-prevent>
          <DialogHeader>
            <DialogTitle>Weekend in Thanjavur</DialogTitle>
            <DialogDescription>
              Every Sat & Sun · only {seatsPerDay} seats a day · ₹2,699 per person, inclusive of taxes.
              Transport is optional and quoted before payment. Choose your group and preferred date below; we&apos;ll confirm seats and the full cost.
            </DialogDescription>
          </DialogHeader>
          {open && <WeekendBookingSection />}
        </DialogContent>
      </Dialog>
    </>
  )
}
